import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';

function roleHome(role) {
  if (role === 'super_admin' || role === 'school_admin' || role === 'partner_admin' || role === 'assistant_admin') return '/admin/dashboard';
  if (role === 'teacher') return '/teacher/dashboard';
  return '/student/dashboard';
}

const WHATSAPP_FIRST_CLASS =
  'https://wa.me/525527780611?text=Hola%2C%20quiero%20agendar%20mi%20primera%20clase%20de%20Chess4Kings.';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user, loading: authLoading } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!authLoading && user) {
      nav(user.must_change_password ? '/change-password' : roleHome(user.role), { replace: true });
    }
  }, [user, authLoading, nav]);

  const submit = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password) {
      toast.error('Escribe tu email y contraseña');
      return;
    }

    setLoading(true);

    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`¡Bienvenido/a, ${u.first_name}!`);

      if (u.must_change_password) {
        nav('/change-password', { replace: true });
      } else {
        nav(roleHome(u.role), { replace: true });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;

      let msg = 'No pudimos iniciar sesión. Inténtalo de nuevo.';

      if (status === 401 || detail === 'Invalid credentials') {
        msg = 'Email o contraseña incorrectos';
      } else if (detail) {
        msg = detail;
      } else if (!err.response) {
        msg = 'Sin conexión con el servidor. Reintenta en unos segundos.';
      }

      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[linear-gradient(180deg,#EAF5FF_0%,#F7FBFF_55%,#E7F1FA_100%)]">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-blue-200/40 blur-3xl" />
        <div className="absolute -right-24 top-20 h-80 w-80 rounded-full bg-blue-300/30 blur-3xl" />
        <div className="absolute bottom-0 left-1/2 h-64 w-[70%] -translate-x-1/2 rounded-full bg-white/70 blur-3xl" />
      </div>

      <div className="relative z-20 flex justify-center px-5 pt-7">
        <Link to="/" className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-lg ring-1 ring-blue-100">
            <img
              src="/branding/characters/Rey-azul-feliz(3).png"
              alt="Chess4Kings"
              className="h-9 w-9 object-contain"
            />
          </div>
          <div>
            <div className="font-display text-xl font-extrabold tracking-tight text-slate-950">
              Chess4Kings
            </div>
            <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-blue-600">
              Academia de ajedrez
            </div>
          </div>
        </Link>
      </div>

      <main className="relative z-10 mx-auto flex min-h-[calc(100vh-100px)] max-w-[1500px] items-center justify-center px-4 py-8 sm:px-8 lg:px-12">
        <div className="relative flex w-full max-w-6xl items-center justify-center">

          <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-[30%] lg:block">
            <div className="absolute left-[2%] top-[24%] w-[92%] rounded-2xl border border-blue-200/80 bg-gradient-to-br from-white/75 via-blue-50/55 to-white/65 px-5 py-4 text-center shadow-[0_12px_30px_-20px_rgba(37,99,235,0.5)] backdrop-blur-sm">
              <div className="mb-3 flex items-center justify-center gap-2">
                <span className="h-px w-8 bg-blue-300" />
                <span className="text-lg leading-none text-blue-600">♟</span>
                <span className="h-px w-8 bg-blue-300" />
              </div>
              <div className="text-sm font-extrabold text-blue-700">
                Aprende a pensar diferente
              </div>
              <div className="mt-1 text-xs leading-relaxed text-slate-500">
                El ajedrez desarrolla habilidades para la vida.
              </div>
            </div>
            <img
              src="/branding/characters/peon-blanco.png"
              alt=""
              className="absolute bottom-[8%] left-[4%] w-24 xl:w-32 drop-shadow-xl"
            />
            <img
              src="/branding/characters/peon-azul.png"
              alt=""
              className="absolute bottom-[2%] left-[28%] w-28 xl:w-36 drop-shadow-xl"
            />
            <img
              src="/branding/characters/Caballo-blanco-feliz.png"
              alt=""
              className="absolute bottom-[4%] left-[55%] w-32 xl:w-40 drop-shadow-xl"
            />
          </div>

          <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[30%] lg:block">
            <div className="absolute right-[2%] top-[24%] w-[92%] rounded-2xl border border-blue-200/80 bg-gradient-to-bl from-white/75 via-blue-50/55 to-white/65 px-5 py-4 text-center shadow-[0_12px_30px_-20px_rgba(37,99,235,0.5)] backdrop-blur-sm">
              <div className="mb-3 flex items-center justify-center gap-2">
                <span className="h-px w-8 bg-blue-300" />
                <span className="text-lg leading-none text-blue-600">♛</span>
                <span className="h-px w-8 bg-blue-300" />
              </div>
              <div className="text-sm font-extrabold text-blue-700">
                Aprende. Practica. Mejora.
              </div>
              <div className="mt-1 text-xs leading-relaxed text-slate-500">
                Tu camino hacia el dominio del ajedrez.
              </div>
            </div>
            <img
              src="/branding/characters/Reina-dorada-feliz(1).png"
              alt=""
              className="absolute bottom-[4%] right-[4%] w-28 xl:w-36 drop-shadow-xl"
            />
            <img
              src="/branding/characters/Torre-blanca-feliz(1).png"
              alt=""
              className="absolute bottom-[5%] right-[30%] w-28 xl:w-36 drop-shadow-xl"
            />
            <img
              src="/branding/characters/Torre-chocolate-enojada(1).png"
              alt=""
              className="absolute bottom-[1%] right-[55%] w-28 xl:w-36 drop-shadow-xl"
            />
          </div>

          <Card className="relative z-30 w-full max-w-[430px] overflow-hidden rounded-[2rem] border border-white/80 bg-white/95 shadow-[0_35px_90px_-30px_rgba(15,72,130,0.45)] backdrop-blur">
            <CardHeader className="px-7 pb-3 pt-8 sm:px-9 sm:pt-10">
              <div className="mb-5 flex justify-center">
                <img
                  src="/branding/characters/logo-chess4kings.png"
                  alt="Chess4Kings"
                  className="h-20 w-auto max-w-[280px] object-contain"
                />
              </div>

              <CardTitle className="text-center font-display text-3xl font-extrabold tracking-tight text-slate-950">
                Iniciar sesión
              </CardTitle>

              <p className="mt-2 text-center text-sm leading-relaxed text-slate-500">
                Accede a tu academia y continúa tu entrenamiento Chess4Kings.
              </p>
            </CardHeader>

            <CardContent className="px-7 pb-8 sm:px-9 sm:pb-10">
              <form onSubmit={submit} noValidate className="space-y-5">
                <div className="space-y-2">
                  <Label className="font-semibold text-slate-700">
                    Email o usuario
                  </Label>
                  <Input
                    data-testid="login-email"
                    type="text"
                    autoComplete="username"
                    placeholder="tu@email.com o tu_usuario"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 rounded-xl border-slate-200 bg-slate-50 px-4 focus-visible:ring-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="font-semibold text-slate-700">
                    Contraseña
                  </Label>
                  <Input
                    data-testid="login-password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 rounded-xl border-slate-200 bg-slate-50 px-4 focus-visible:ring-blue-500"
                  />
                </div>

                <Button
                  data-testid="login-submit"
                  type="submit"
                  disabled={loading}
                  className="h-12 w-full rounded-xl bg-blue-600 font-bold shadow-lg shadow-blue-600/20 hover:bg-blue-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Entrando…
                    </>
                  ) : (
                    <>
                      Entrar
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <div className="flex justify-center">
                  <Link
                    to="/forgot-password"
                    className="text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              </form>

              <div className="mt-8 border-t border-slate-100 pt-6 text-center">
                <p className="text-sm text-slate-500">
                  ¿Aún no eres alumno de Chess4Kings?
                </p>
                <a
                  href={WHATSAPP_FIRST_CLASS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center font-bold text-blue-600 hover:text-blue-700"
                >
                  Agenda tu primera clase
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <div className="relative z-20 pb-5 text-center">
        <Link
          to="/"
          className="text-sm font-semibold text-slate-400 transition-colors hover:text-blue-600"
        >
          ← Volver a Chess4Kings
        </Link>
      </div>
    </div>
  );
}
